import { styled } from "styled-components";
import { HashLink } from "react-router-hash-link";
import { useLocation, useMatch } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { IoCodeOutline } from "react-icons/io5";
// import { FiArrowUpRight } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  {
    placeholder: "Home",
    path: "/",
  },
  {
    placeholder: "Projects",
    path: "/projects",
  },
  {
    placeholder: "Contact",
    path: "#contact",
  },
];

export function HomeNavigation() {
  const location = useLocation();

  const isProjectsRoot = useMatch("/projects");
  const isProjectsNested = useMatch("/projects/:projectId");

  const mobileRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [homeDetailsVisible, setHomeDetailsVisible] = useState(() =>
    typeof window === "undefined"
      ? true
      : window.location.pathname !== "/" || window.__homeDetailsRevealStarted,
  );

  const selected =
    isProjectsRoot || isProjectsNested
      ? "Projects"
      : location.pathname === "/"
        ? "Home"
        : null;

  const getNavPath = (item) => {
    if (item.placeholder === "Contact") {
      return `${location.pathname}#contact`;
    }

    return item.path;
  };

  useEffect(() => {
    if (location.pathname !== "/") {
      setHomeDetailsVisible(true);
      return undefined;
    }

    setHomeDetailsVisible(Boolean(window.__homeDetailsRevealStarted));

    const showWithHomeDetails = () => {
      setHomeDetailsVisible(true);
    };

    const hideBeforeHomeReveal = () => {
      if (window.location.pathname === "/") {
        setHomeDetailsVisible(false);
      }
    };

    window.addEventListener("home-details-reveal:start", showWithHomeDetails);
    window.addEventListener("page-transition:start", hideBeforeHomeReveal);

    return () => {
      window.removeEventListener(
        "home-details-reveal:start",
        showWithHomeDetails,
      );
      window.removeEventListener("page-transition:start", hideBeforeHomeReveal);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 70);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setHovered(false);
    setMobileOpen(false);

    const frame = requestAnimationFrame(() => {
      setScrolled(window.scrollY > 70);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");

    const update = () => {
      setIsMobile(media.matches);

      if (!media.matches) {
        setMobileOpen(false);
      }
    };

    update();

    media.addEventListener("change", update);

    return () => {
      media.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const handlePointerDown = (event) => {
      if (mobileRef.current && !mobileRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [mobileOpen]);

  const handleNavClick = () => {
    setHovered(false);
    setMobileOpen(false);
  };

  const expanded = !scrolled || hovered;
  const revealClass =
    location.pathname === "/" && !homeDetailsVisible ? "home-hidden" : "";

  if (isMobile) {
    return (
      <MobileNavigationContainer className={`wrap ${revealClass}`}>
        <div className="mobile-nav" ref={mobileRef}>
          <motion.button
            className="mobile-trigger"
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((prev) => !prev)}
            whileTap={{ scale: 0.94 }}
          >
            <IoCodeOutline />
          </motion.button>

          <AnimatePresence>
            {mobileOpen && (
              <motion.div
                className="mobile-menu"
                initial={{
                  opacity: 0,
                  y: -8,
                  scale: 0.96,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  y: -8,
                  scale: 0.96,
                }}
                transition={{
                  duration: 0.22,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {navItems.map((item) => {
                  const active =
                    item.placeholder !== "Contact" &&
                    selected === item.placeholder;

                  return (
                    <HashLink
                      key={item.placeholder}
                      smooth
                      className={active ? "active" : ""}
                      to={getNavPath(item)}
                      onClick={handleNavClick}
                    >
                      {item.placeholder}
                    </HashLink>
                  );
                })}

                {/* <span className="mobile-divider" />

                <a
                  href="/resume.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="mobile-resume"
                  onClick={() => setMobileOpen(false)}
                >
                  Resume
                  <FiArrowUpRight />
                </a> */}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </MobileNavigationContainer>
    );
  }

  return (
    <HomeNavigationContainer className={`wrap ${revealClass}`}>
      <motion.nav
        className="nav-container"
        onMouseEnter={() => {
          if (scrolled) {
            setHovered(true);
          }
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
        initial={false}
        animate={{
          paddingLeft: expanded ? 8 : 6,
          paddingRight: expanded ? 8 : 6,
        }}
        transition={{
          duration: 0.42,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <motion.div
          className="collapsed-icon"
          initial={false}
          animate={{
            width: expanded ? 0 : 42,
            opacity: expanded ? 0 : 1,
            scale: expanded ? 0.8 : 1,
          }}
          transition={{
            width: {
              duration: 0.42,
              ease: [0.22, 1, 0.36, 1],
            },
            opacity: {
              duration: 0.18,
            },
            scale: {
              duration: 0.25,
            },
          }}
        >
          <IoCodeOutline />
        </motion.div>

        <motion.div
          className="nav-content"
          initial={false}
          animate={{
            maxWidth: expanded ? 500 : 0,
            opacity: expanded ? 1 : 0,
          }}
          transition={{
            maxWidth: {
              duration: 0.42,
              ease: [0.22, 1, 0.36, 1],
            },
            opacity: {
              duration: expanded ? 0.25 : 0.12,
              delay: expanded ? 0.05 : 0,
            },
          }}
        >
          <ul>
            {navItems.map((item) => {
              const active =
                item.placeholder !== "Contact" && selected === item.placeholder;

              return (
                <li key={item.placeholder}>
                  <HashLink
                    smooth
                    className={active ? "active" : ""}
                    to={getNavPath(item)}
                    onClick={handleNavClick}
                  >
                    {item.placeholder}
                  </HashLink>
                </li>
              );
            })}
          </ul>

          {/* <span className="divider" />

          <a
            href="/resume.pdf"
            target="_blank"
            rel="noreferrer"
            className="resume-link"
          >
            Resume
            <FiArrowUpRight />
          </a> */}
        </motion.div>
      </motion.nav>
    </HomeNavigationContainer>
  );
}

const HomeNavigationContainer = styled.div`
  position: fixed;
  top: 23px;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 99;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1);

  &.home-hidden {
    opacity: 0;
    pointer-events: none;
  }

  .nav-container {
    pointer-events: auto;
    min-height: 52px;
    display: flex;
    align-items: center;
    overflow: hidden;
    border-radius: 20px;
    background: rgba(28, 28, 32, 0.72);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.14);
    box-shadow:
      0 12px 40px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.09),
      inset 0 -1px 0 rgba(255, 255, 255, 0.025);
  }

  .collapsed-icon {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    color: #abababff;
    cursor: pointer;

    svg {
      flex-shrink: 0;
      font-size: 20px;
    }
  }

  .nav-content {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    white-space: nowrap;
  }

  ul {
    display: flex;
    align-items: center;
    gap: 2px;
    list-style: none;
    margin: 0;
    padding: 0;
    flex-shrink: 0;
  }

  li {
    list-style: none;
  }

  a {
    font-family: "Poppins", sans-serif;
    font-size: 14px;
    font-weight: 300;
    text-decoration: none;
    color: #abababff;
    transition:
      color 0.2s ease,
      background 0.2s ease;
  }

  ul a {
    height: 40px;
    display: flex;
    align-items: center;
    padding: 0 15px;
    border-radius: 12px;

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.08);
    }

    &.active {
      color: #fff;
      background: rgba(255, 255, 255, 0.1);
    }
  }

  .divider {
    width: 1px;
    height: 20px;
    flex-shrink: 0;
    margin: 0 3px;
    background: rgba(255, 255, 255, 0.2);
  }

  .resume-link {
    height: 40px;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 12px;
    flex-shrink: 0;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 300;
    color: #abababff;

    svg {
      font-size: 14px;
      transition: transform 0.2s ease;
    }

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.08);

      svg {
        transform: translate(2px, -2px);
      }
    }
  }

  @media (max-width: 768px) {
    top: 20px;
  }
`;

const MobileNavigationContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 99;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1);

  &.home-hidden {
    opacity: 0;
    pointer-events: none;
  }

  .mobile-nav {
    position: relative;
    pointer-events: auto;
  }

  .mobile-trigger {
    width: 44px;
    height: 44px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: rgba(28, 28, 32, 0.78);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    box-shadow:
      0 12px 35px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.09);
    color: #abababff;
    cursor: pointer;

    svg {
      font-size: 18px;
    }
  }

  .mobile-menu {
    position: absolute;
    top: 54px;
    right: 0;
    width: 155px;
    display: flex;
    flex-direction: column;
    padding: 6px;
    border-radius: 12px;
    background: rgba(28, 28, 32, 0.86);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.14);
    box-shadow:
      0 16px 40px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  .mobile-menu a {
    min-height: 38px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    border-radius: 8px;
    font-family: "Poppins", sans-serif;
    font-size: 12px;
    font-weight: 300;
    text-decoration: none;
    color: #abababff;

    &.active,
    &:active {
      color: #fff;
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .mobile-divider {
    width: 100%;
    height: 1px;
    margin: 5px 0;
    background: rgba(255, 255, 255, 0.12);
  }

  .mobile-resume {
    display: flex;
    justify-content: space-between;
    gap: 8px;

    svg {
      font-size: 11px;
      flex-shrink: 0;
    }
  }
`;
